import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { EventFormData, ValidationError } from 'simple-creator/types';
import { DraftSaveStatus } from '../services/draftService';

interface FormState {
  // Form data
  formData: EventFormData;
  currentStep: number;
  isDirty?: boolean;
  isSubmitting: boolean;
  
  // Validation
  errors: Record<string, ValidationError | undefined>;
  touchedFields: Set<string>;
  stepValidation: Record<number, boolean>;
  
  // Draft management
  draftId: string | null;
  lastSaved: Date | null;
  saveStatus: DraftSaveStatus;
  
  // Actions
  setDraftId: (draftId: string | null) => void;
  setSaveStatus: (status: DraftSaveStatus) => void;
  setLastSaved: (date: Date | null) => void;
  updateField: <K extends keyof EventFormData>(
    field: K,
    value: EventFormData[K],
    markDirty?: boolean
  ) => void;
  setStep: (step: number) => void;
  nextStep: () => void;
  previousStep: () => void;
  setErrors: (errors: Record<string, ValidationError>) => void;
  touchField: (field: string) => void;
  setStepValidation: (step: number, isValid: boolean) => void;
  initializeForm: (data: EventFormData) => void;
  resetForm: () => void;
  markClean: () => void;
  saveDraft: () => Promise<string>;
  loadDraft: (draftId: string) => Promise<void>;
  submit: () => Promise<void>;
}

const initialFormData: EventFormData = {
  title: '',
  categories: [],
  dates: [],
  location: {
    name: '',
    address: '',
    coordinates: { lat: 0, lng: 0 },
  },
  images: [],
  isDraft: true,
};

export const useFormStore = create<FormState>()(
  devtools(
    persist(
      (set, get) => ({
        // Initial state
        formData: initialFormData,
        currentStep: 0,
        isDirty: false,
        isSubmitting: false,
        errors: {},
        touchedFields: new Set(),
        stepValidation: {},
        draftId: null,
        lastSaved: null,
        saveStatus: 'idle' as DraftSaveStatus,
        
        // Draft state setters
        setDraftId: (draftId) => set({ draftId }),
        setSaveStatus: (saveStatus) => set({ saveStatus }),
        setLastSaved: (lastSaved) => set({ lastSaved }),
        
        // Field update
        updateField: (field, value, markDirty = true) => {
          set((state) => ({
            formData: {
              ...state.formData,
              [field]: value,
            },
            isDirty: markDirty ? true : state.isDirty,
            errors: {
              ...state.errors,
              [field]: undefined, // Clear error when field is updated
            },
          }));
        },
        
        // Step navigation
        setStep: (step) => set({ currentStep: step }),
        
        nextStep: () => {
          set((state) => ({
            currentStep: Math.min(state.currentStep + 1, 4),
          }));
        },
        
        previousStep: () => {
          set((state) => ({
            currentStep: Math.max(state.currentStep - 1, 0),
          }));
        },
        
        // Validation
        setErrors: (errors) => set({ errors }),
        
        touchField: (field) => {
          set((state) => ({
            touchedFields: new Set([...state.touchedFields, field]),
          }));
        },
        
        setStepValidation: (step, isValid) => {
          set((state) => ({
            stepValidation: {
              ...state.stepValidation,
              [step]: isValid,
            },
          }));
        },
        
        // Form initialization
        initializeForm: (data) => {
          set({
            formData: data,
            currentStep: 0,
            isDirty: false,
            isSubmitting: false,
            errors: {},
            touchedFields: new Set(),
            stepValidation: {},
            draftId: data.draftId || null,
          });
        },
        
        resetForm: () => {
          set({
            formData: initialFormData,
            currentStep: 0,
            isDirty: false,
            isSubmitting: false,
            stepValidation: {},
            errors: {},
            touchedFields: new Set(),
            draftId: null,
            lastSaved: null,
            saveStatus: 'idle' as DraftSaveStatus,
          });
        },

        markClean: () => {
          set({ isDirty: false });
        },
        
        // Draft management
        saveDraft: async () => {
          const { formData, draftId } = get();
          const id = draftId || `draft_${Date.now()}`;
          
          try {
            // Save to localStorage
            localStorage.setItem(
              `sc_draft_${id}`,
              JSON.stringify({
                ...formData,
                draftId: id,
                savedAt: new Date().toISOString(),
              })
            );
            
            set({
              draftId: id,
              lastSaved: new Date(),
              isDirty: false,
            });
            
            return id;
          } catch (error) {
            console.error('Failed to save draft:', error);
            throw error;
          }
        },
        
        loadDraft: async (draftId) => {
          try {
            const stored = localStorage.getItem(`sc_draft_${draftId}`);
            if (stored) {
              const draft = JSON.parse(stored) as EventFormData;
              get().initializeForm(draft);
            } else {
              throw new Error(`Draft ${draftId} not found`);
            }
          } catch (error) {
            console.error('Failed to load draft:', error);
            throw error;
          }
        },
        
        // Form submission
        submit: async () => {
          set({ isSubmitting: true });
          
          try {
            const { formData } = get();
            
            // TODO: Call API to create/update event
            console.log('Submitting form:', formData);
            
            // Simulate API call
            await new Promise((resolve) => setTimeout(resolve, 1000));
            
            // Clear draft after successful submission
            const { draftId } = get();
            if (draftId) {
              localStorage.removeItem(`sc_draft_${draftId}`);
            }
            
            set({
              isSubmitting: false,
              isDirty: false,
            });
          } catch (error) {
            set({ isSubmitting: false });
            throw error;
          }
        },
      }),
      {
        name: 'simple-creator-form',
        partialize: (state) => ({
          draftId: state.draftId,
          lastSaved: state.lastSaved,
        }),
      }
    )
  )
);
